import axios from 'axios';

export const instance = axios.create({
    baseURL: `${process.env.REACT_APP_API_URL}/api/v1`,
});

let refreshPromise = null;

export const refreshAccessToken = () => {
    if (refreshPromise) {
        return refreshPromise;
    }

    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
        return Promise.reject(new Error('Refresh token ausente.'));
    }

    refreshPromise = axios.post(`${process.env.REACT_APP_API_URL}/token/refresh`, {
        refresh: refreshToken
    })
        .then(({ data }) => {
            localStorage.setItem('accessToken', data.access);

            if (data.refresh) {
                localStorage.setItem('refreshToken', data.refresh);
            }

            return data.access;
        })
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
};

export const getWebSocketToken = async () => {
    const { data } = await instance.post('/ws-token/');

    return data.token;
};

instance.interceptors.request.use(config => {
    const accessToken = localStorage.getItem('accessToken');

    config.headers.Authorization = `Bearer ${accessToken}`;
  
    return config;
});

instance.interceptors.response.use(
    response => response,
    async error => {
        // inicia a revalidação do token de acesso:
        if (error.response?.status === 401 && !error.config?._retry) {
            try {
                const accessToken = await refreshAccessToken();
                error.config._retry = true;
                error.config.headers.Authorization = `Bearer ${accessToken}`;

                return instance(error.config);
            } catch {
                localStorage.removeItem('user');
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
    
                window.location.href = `${process.env.REACT_APP_EGIDE_URL}/logout`;

                return Promise.reject(error);
            }
        }

        return Promise.reject(error);
    }
);

const DEFAULT_CONFIG = {
    noPrefix: false,
};

const Api = (resource, config=DEFAULT_CONFIG) => {
    resource = !process.env.REACT_APP_DEBUG
        ? resource.replace('http://', 'https://')
        : resource;

    return {
        /**
         * Busca os registros do recurso.
         * 
         * @param {Object} params
         */
        index: (params = {}) => instance.get(config.noPrefix ? `${process.env.REACT_APP_API_URL}/${resource}` : resource, { params }),
    
        /**
         * Persiste o novo recurso.
         * 
         * @param {Object} data
         * @param {Object} props
         */
        store: (data, props={}) => {
            if (props.headers) {
                props.headers = {
                    ...config.headers,
                    ...props.headers
                };
            }
    
            return instance.post(`${resource}/`, data, props);
        },
    
        /**
         * Busca o registro especificado.
         * 
         * @param {Number} id
         */
        show: id => instance.get(`${resource}/${id}`),
    
        /**
         * Atualiza o recurso especificado.
         * 
         * @param {Number} id 
         * @param {Object} data
         */
        update: (id, data) => instance.put(`${resource}/${id}/`, data),

        /**
         * Atualiza parcialmente o recurso especificado.
         * 
         * @param {Number} id 
         * @param {Object} data
         * @param {Object} props
         */
        patch: (id, data, props = {}) => instance.patch(`${resource}/${id}/`, data, props),
    
        /**
         * Exclui o recurso especificado.
         * 
         * @param {Number} id 
         */
        delete: id => instance.delete(resource + (id ? `/${id}/` : '')),
    };
};

export default Api;
